import type { AxiosResponse } from 'axios';
import api from 'src/services/api';
import * as utils from './apiUtils';
import type { UserLoginParams } from 'src/models/apis/user/userRequest';
import {
  type UserLoginResponse,
  type UserLogoutResponse,
  type UserGetMeResponse,
  toUser,
  toUserLogin,
  toUserLogout,
} from 'src/models/apis/user/userResponse';
import type { User, UserLogin, UserLogout } from 'src/models/user';
import type {
  AdminUserIndexParams,
  AdminUserCreateParams,
  AdminUserUpdateParams,
  AdminUserPasswordUpdateParams,
} from 'src/models/apis/user/adminUserRequest';
import {
  type AdminUserIndexResponse,
  type AdminUserBaseResponse,
  type AdminUserUpdateResponse,
  type AdminUserDestroyResponse,
  type AdminUserUploadCsvResponse,
  toAdminUser,
  toAdminUserBase,
  toAdminUserUpdate,
  toAdminUserDestroy,
  toAdminUserUploadCsv,
} from 'src/models/apis/user/adminUserResponse';
import type {
  AdminUserUploadCsv,
  AdminUser,
  AdminUserBase,
  AdminUserUpdate,
  AdminUserDestroy,
} from 'src/models/user';

export default {
  async login(credentials: UserLoginParams): Promise<UserLogin> {
    const response = await api.post<UserLoginResponse>(utils.getApiUrl('/users/login'), credentials);
    return toUserLogin(response.data);
  },
  async logout(): Promise<UserLogout> {
    const response = await api.delete<UserLogoutResponse>(utils.getApiUrl('/users/logout'));
    return toUserLogout(response.data);
  },
  async getMe(): Promise<User> {
    const response = await api.get<UserGetMeResponse>(utils.getApiUrl('/users/me'));
    return toUser(response.data);
  },
  async adminIndex(params: AdminUserIndexParams): Promise<AdminUser[]> {
    const response = await api.get<AdminUserIndexResponse>(utils.getApiUrl('/admin/users'), { params });
    return response.data.map(toAdminUser);
  },
  async adminCreate(params: AdminUserCreateParams): Promise<AdminUserBase> {
    const response = await api.post<AdminUserBaseResponse>(utils.getApiUrl('/admin/users'), params);
    return toAdminUserBase(response.data);
  },
  async adminUpdate(id: number, params: AdminUserUpdateParams): Promise<AdminUserUpdate> {
    const response = await api.put<AdminUserUpdateResponse>(utils.getApiUrl(`/admin/users/${id}`), params);
    return toAdminUserUpdate(response.data);
  },
  async adminPasswordUpdate(
    id: number,
    params: AdminUserPasswordUpdateParams,
  ): Promise<AdminUserUpdate> {
    const response = await api.put<AdminUserUpdateResponse>(utils.getApiUrl(`/admin/users/password/${id}`), params);
    return toAdminUserUpdate(response.data);
  },
  async adminDestroy(id: number): Promise<AdminUserDestroy> {
    const response = await api.delete<AdminUserDestroyResponse>(utils.getApiUrl(`/admin/users/${id}`));
    return toAdminUserDestroy(response.data);
  },
  adminDownloadCSV(params: AdminUserIndexParams): Promise<AxiosResponse<Blob>> {
    return api.get(utils.getApiUrl(`/admin/users/csv`), { params, responseType: 'blob' });
  },
  async adminUploadCSV(params: FormData): Promise<AdminUserUploadCsv> {
    const response = await api.post<AdminUserUploadCsvResponse>(utils.getApiUrl(`/admin/users/csv`), params);
    return toAdminUserUploadCsv(response.data);
  },
};
